import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { BookOpen, ArrowRight, Clock, Calendar, User, Library } from 'lucide-react';
import { api } from '../../../../shared/api/api.js';
import type { Post } from '../../api/blogApi.js';
import { calculateReadingTime } from './blogPostUtils.js';
import { resolveCoverUrl } from '../../../../shared/utils/imageCompressor.js';

interface BlogRelatedArticlesProps {
  currentPostId: string;
  currentCategory?: string | null;
  onSelectPost?: (slug: string) => void;
}

export const BlogRelatedArticles: React.FC<BlogRelatedArticlesProps> = ({
  currentPostId,
  currentCategory,
  onSelectPost,
}) => {
  const { data: posts = [], isLoading } = useQuery({
    queryKey: ['blog-published-posts'],
    queryFn: () => api.getPublishedPosts(),
  });

  // Filter out current post and rank by category match
  const otherPosts = posts.filter((p) => p.id !== currentPostId);

  if (otherPosts.length === 0) return null;

  // Match same category first
  const sameCategoryPosts = currentCategory
    ? otherPosts.filter((p) => p.category === currentCategory)
    : [];
  
  // Combine: same category posts first, then fill with other posts until 3 items
  const combined = [...sameCategoryPosts];
  for (const post of otherPosts) {
    if (combined.length >= 3) break;
    if (!combined.some((p) => p.id === post.id)) {
      combined.push(post);
    }
  }

  const relatedPosts = combined.slice(0, 3);

  if (relatedPosts.length === 0) return null;

  const handleArticleClick = (slug: string) => {
    if (onSelectPost) {
      onSelectPost(slug);
    } else {
      window.history.pushState({}, '', `/blog/${slug}`);
      window.dispatchEvent(new PopStateEvent('popstate'));
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const formatDate = (dateStr?: string | null) => {
    if (!dateStr) return '';
    try {
      return new Date(dateStr).toLocaleDateString('id-ID', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
      });
    } catch {
      return dateStr;
    }
  };

  return (
    <div className="mt-16 pt-12 border-t border-slate-100 space-y-6">
      {/* Section Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 pb-2">
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center shrink-0">
            <Library className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold text-slate-900 tracking-tight">
              Artikel Terkait
            </h3>
            <p className="text-xs text-slate-500">
              Rekomendasi bacaan berdasarkan {currentCategory ? `kategori "${currentCategory}"` : 'topik terpopuler'}
            </p>
          </div>
        </div>

        {currentCategory && (
          <span className="self-start sm:self-auto text-[10px] font-black text-indigo-700 bg-indigo-50 border border-indigo-100 px-3 py-1 rounded-full uppercase tracking-wider">
            {currentCategory}
          </span>
        )}
      </div>

      {/* Cards Grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 animate-pulse">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-64 bg-slate-100 rounded-2xl" />
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {relatedPosts.map((post) => {
            const readingTime = calculateReadingTime(post.content);
            const isCategoryMatched = Boolean(currentCategory && post.category === currentCategory);

            return (
              <div
                key={post.id}
                onClick={() => handleArticleClick(post.slug)}
                className="bg-slate-50 hover:bg-white rounded-2xl p-4 sm:p-5 border border-slate-200/80 hover:border-indigo-300 transition-all duration-300 cursor-pointer hover:shadow-xl group flex flex-col justify-between"
              >
                <div className="space-y-3">
                  {/* Image / Fallback Header */}
                  <div className="aspect-video w-full rounded-xl overflow-hidden bg-gradient-to-br from-indigo-900 via-slate-900 to-indigo-950 relative shadow-2xs">
                    {post.coverUrl ? (
                      <img
                        src={resolveCoverUrl(post.coverUrl)}
                        alt={post.title}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-white/40 p-4 text-center">
                        <BookOpen className="w-8 h-8 text-indigo-400 mb-1" />
                        <span className="text-[10px] font-mono text-indigo-200 uppercase tracking-widest line-clamp-1">
                          {post.category || 'UIBN Insight'}
                        </span>
                      </div>
                    )}

                    {/* Category Pill */}
                    <div className="absolute top-2.5 left-2.5 flex items-center gap-1.5">
                      <span className="px-2.5 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-slate-900/80 backdrop-blur-md text-white border border-white/20 shadow-xs">
                        {post.category || 'Umum'}
                      </span>
                      {isCategoryMatched && (
                        <span className="px-2 py-0.5 rounded-full text-[9px] font-bold bg-emerald-500 text-white shadow-2xs">
                          Relevan
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Title & Subtitle */}
                  <div>
                    <h4 className="font-bold text-slate-900 text-sm sm:text-base line-clamp-2 group-hover:text-indigo-600 transition-colors leading-snug">
                      {post.title}
                    </h4>
                    <p className="text-xs text-slate-500 line-clamp-2 mt-1.5 leading-relaxed">
                      {post.summary || post.subtitle || 'Baca wawasan terkini industri perbukuan & UIBN.'}
                    </p>
                  </div>
                </div>

                {/* Footer Meta & Action */}
                <div className="mt-5 pt-3 border-t border-slate-200/80 flex items-center justify-between text-[11px] text-slate-500 font-medium">
                  <div className="flex items-center gap-2 truncate pr-2">
                    <span className="flex items-center gap-1 truncate text-slate-700 font-semibold">
                      <User className="w-3 h-3 text-indigo-600 shrink-0" />
                      <span className="truncate">{post.authorName}</span>
                    </span>
                    <span>•</span>
                    <span className="flex items-center gap-1 shrink-0 text-slate-400">
                      <Clock className="w-3 h-3" />
                      <span>{readingTime} min</span>
                    </span>
                  </div>

                  <span className="inline-flex items-center gap-1 text-indigo-600 font-bold group-hover:translate-x-1 transition-transform shrink-0">
                    <span>Baca</span>
                    <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};
